<template>
  <nav class="pagination" aria-label="Pagination">
    <ul class="pagination__list">
      <li class="pagination__item">
        <a
          v-if="currentPage > 1"
          :href="getPageUrl(currentPage - 1)"
          class="pagination__link pagination__link--prev"
          aria-label="Previous page"
        >
          &laquo; Prev
        </a>
        <span
          v-else
          class="pagination__link pagination__link--disabled"
          aria-disabled="true"
        >
          &laquo; Prev
        </span>
      </li>

      <li
        v-for="page in visiblePages"
        :key="page"
        class="pagination__item"
      >
        <span
          v-if="page === '...'"
          class="pagination__ellipsis"
        >
          ...
        </span>
        <a
          v-else-if="page !== currentPage"
          :href="getPageUrl(page)"
          class="pagination__link"
          :aria-label="`Go to page ${page}`"
        >
          {{ page }}
        </a>
        <span
          v-else
          class="pagination__link pagination__link--current"
          aria-current="page"
        >
          {{ page }}
        </span>
      </li>

      <li class="pagination__item">
        <a
          v-if="currentPage < totalPages"
          :href="getPageUrl(currentPage + 1)"
          class="pagination__link pagination__link--next"
          aria-label="Next page"
        >
          Next &raquo;
        </a>
        <span
          v-else
          class="pagination__link pagination__link--disabled"
          aria-disabled="true"
        >
          Next &raquo;
        </span>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    baseUrl: {
      type: String,
      required: true,
    },
    queryMode: {
      type: Boolean,
      default: false,
    },
    queryKey: {
      type: String,
      default: 'page',
    },
  },
  computed: {
    visiblePages() {
      const pages = []
      const delta = 2

      for (let i = 1; i <= this.totalPages; i++) {
        if (
          i === 1 ||
          i === this.totalPages ||
          (i >= this.currentPage - delta &&
            i <= this.currentPage + delta)
        ) {
          pages.push(i)
        } else if (
          pages[pages.length - 1] !== '...'
        ) {
          pages.push('...')
        }
      }

      return pages
    },
  },
  methods: {
    getPageUrl(page) {
      if (this.queryMode) {
        if (page === 1) {
          return this.baseUrl
        }
        return `${this.baseUrl}?${this.queryKey}=${page}`
      }
      if (page === 1) {
        return this.baseUrl
      }
      return `${this.baseUrl}page/${page}/`
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.pagination {
  margin: $spacing-xl 0;
}

.pagination__list {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: $spacing-xs;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
}

.pagination__item {
  display: flex;
}

.pagination__link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 $spacing-sm;
  color: $link-color;
  text-decoration: none;
  border: 1px solid $border-color;
  border-radius: 4px;
  background-color: $bg-primary;
  transition: all 0.2s ease;

  &:hover:not(&--disabled):not(&--current) {
    background-color: $primary-color;
    color: $bg-primary;
    border-color: $primary-color;
  }

  &--current {
    background-color: $primary-color;
    color: $bg-primary;
    border-color: $primary-color;
    font-weight: bold;
    cursor: default;
  }

  &--disabled {
    color: $text-secondary;
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--prev,
  &--next {
    font-weight: 500;
  }
}

.pagination__ellipsis {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  color: $text-secondary;
}

@include mobile {
  .pagination__link {
    min-width: 36px;
    height: 36px;
    font-size: $font-size-sm;
  }

  .pagination__ellipsis {
    min-width: 36px;
    height: 36px;
  }
}
</style>
